/**
 * How a user is named on the Product Access screen.
 *
 * `name` is nullable in the API payload, and the screen used to render it raw. That
 * produced a blank identity line (PA-03), an aria-label reading "… access for null"
 * (PA-04) and a dialog opening "null gets access straight away." (PA-05). Every place
 * that names a user goes through here so the three cannot drift apart again.
 */

const clean = (value) => (typeof value === 'string' ? value.trim() : '');

/**
 * What to call this user. Email is the fallback because it is the only other
 * identifier an admin can act on; the last resort exists so nothing ever renders
 * the word "null" or an empty line.
 */
export const displayNameFor = (user) =>
  clean(user?.name) || clean(user?.email) || 'Unnamed user';

/**
 * The organisation segment of the identity line. Search only returns users who
 * belong to a live organisation, so a blank name means the organisation itself is
 * unnamed (PA-07) — not that the user has none. Saying so keeps the identity line
 * consistent with the "Not enabled for organization" gate note under the switch,
 * which would otherwise refer to an organisation the row never mentions.
 */
export const organisationLabelFor = (user) =>
  clean(user?.organisation_name) || 'Unnamed organization';

/**
 * The muted second line: email, organisation, and the verification caveat.
 * The email is dropped when it is already serving as the display name, so a
 * nameless user does not show the same address twice.
 */
export const secondaryLineFor = (user) => {
  const email = clean(user?.email);
  const parts = [];
  if (email && email !== displayNameFor(user)) parts.push(email);
  parts.push(organisationLabelFor(user));
  if (user?.verified === false) parts.push('not verified');
  return parts.join(' · ');
};
